import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router';
import {
  Zap,
  Plus,
  Upload,
  Download,
  RefreshCw,
  AlertCircle,
  X,
  Pencil,
  Check,
  SkipForward,
  RotateCcw,
  CheckCircle2,
  FileCode2,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ClipboardPaste,
  Copy,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  createGenerationLog,
  createTemplateSet,
  updateTemplateSet,
  getTemplateSetsByProject,
} from '@/lib/api';
import {
  generateAdGroups,
  buildCSV,
  type GeneratedAdGroup,
  type FieldError,
  type FieldType,
  type ErrorType,
  type ExportOptions,
} from '@/lib/generator';
import type {
  TemplateSet,
  TemplateSetContent,
  HeadlineSlot,
  DescriptionSlot,
  KeywordItem,
  KeywordMatchType,
  DataRow,
} from '@/types';
import { CHARACTER_LIMITS, getEffectiveAds } from '@/types';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function detectPlaceholders(texts: string[]): string[] {
  const all = texts.join(' ');
  const matches = all.match(/\{([^}:]+)\}/g) ?? [];
  return [...new Set(matches.map(m => m.slice(1, -1).trim()).filter(Boolean))];
}

function parseCSVRow(line: string): string[] {
  const result: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      result.push(cur.trim()); cur = '';
    } else { cur += ch; }
  }
  result.push(cur.trim());
  return result;
}

function buildEphemeralTemplateSet(
  projectId: string,
  name: string,
  allAds: { headlines: string[]; descriptions: string[] }[],
  keywords: { text: string; matchTypes: KeywordMatchType[] }[],
  campaignName: string,
  adGroupName: string,
  finalUrl: string,
  path1: string,
  path2: string,
  groupingField: string,
): TemplateSet {
  const buildSlots = (adHeadlines: string[], adDescriptions: string[]) => {
    const headlineSlots: HeadlineSlot[] = Array.from({ length: 15 }, (_, i) => ({
      slot_number: i + 1,
      variants: [{ variant_order: 1, template_string: adHeadlines[i] ?? '', enabled: !!(adHeadlines[i]?.trim()), notes: '' }],
    }));
    const descSlots: DescriptionSlot[] = Array.from({ length: 4 }, (_, i) => ({
      slot_number: i + 1,
      variants: [{ variant_order: 1, template_string: adDescriptions[i] ?? '', enabled: !!(adDescriptions[i]?.trim()), notes: '' }],
    }));
    return { headlineSlots, descSlots };
  };
  const ad1 = buildSlots(allAds[0]?.headlines ?? [], allAds[0]?.descriptions ?? []);
  const kws: KeywordItem[] = keywords
    .filter(k => k.text.trim())
    .map((k, i) => ({
      id: `qkw-${i}`,
      variants: [{ variant_order: 1, template_string: k.text, enabled: true, notes: '' }],
      match_types: k.matchTypes.length > 0 ? k.matchTypes : ['broad' as KeywordMatchType],
    }));
  const adsArray = allAds.length > 1
    ? allAds.map((a, idx) => {
        const { headlineSlots, descSlots } = buildSlots(a.headlines, a.descriptions);
        return { ad_index: idx + 1, headlines: headlineSlots, descriptions: descSlots };
      })
    : undefined;
  const content: TemplateSetContent = {
    campaign_grouping_field: groupingField || 'id',
    primary_grouping_field: groupingField || 'id',
    campaign_name_template: campaignName || `{${groupingField || 'id'}}`,
    ad_group_name_template: adGroupName || 'Ad Group',
    campaign_status: 'Enabled',
    ad_group_status: 'Enabled',
    headlines: ad1.headlineSlots,
    descriptions: ad1.descSlots,
    ...(adsArray ? { ads: adsArray } : {}),
    keywords: kws,
    final_url_template: finalUrl,
    path1_template: path1,
    path2_template: path2,
    callouts: [],
    sitelinks: [],
    generate_accent_stripped_keywords: false,
  };
  const now = new Date().toISOString();
  return {
    id: `quick-${Date.now()}`,
    project_id: projectId,
    name,
    language_code: 'en',
    status: 'draft',
    generation_settings: { filters: [] },
    template_content: content,
    created_at: now,
    updated_at: now,
  };
}

// ─── Error helpers ─────────────────────────────────────────────────────────────

function fieldKey(err: FieldError): string | null {
  if (err.field === 'headline' && err.slotNumber != null) return `h${err.slotNumber}`;
  if (err.field === 'description' && err.slotNumber != null) return `d${err.slotNumber}`;
  if (err.field === 'keyword' && err.keywordId) return `kw_${err.keywordId}`;
  if (err.field === 'url') return 'url';
  if (err.field === 'path1') return 'path1';
  if (err.field === 'path2') return 'path2';
  if (err.field === 'campaign') return 'campaign';
  if (err.field === 'ad_group') return 'ad_group';
  return null;
}

function getValueFromAg(ag: GeneratedAdGroup, key: string): string {
  if (key === 'campaign') return ag.campaignName;
  if (key === 'ad_group') return ag.adGroupName;
  if (key === 'url') return ag.finalUrl;
  if (key === 'path1') return ag.path1;
  if (key === 'path2') return ag.path2;
  if (key.startsWith('h')) {
    const n = parseInt(key.slice(1));
    return ag.headlines.find(h => h.slotNumber === n)?.rawText ?? '';
  }
  if (key.startsWith('d')) {
    const n = parseInt(key.slice(1));
    return ag.descriptions.find(d => d.slotNumber === n)?.rawText ?? '';
  }
  if (key.startsWith('kw_')) {
    return ag.keywords.find(k => `kw_${k.keywordId}` === key)?.text ?? '';
  }
  return '';
}

function fieldLabel(field: FieldType, slotNumber?: number): string {
  if (field === 'headline') return `Headline ${slotNumber}`;
  if (field === 'description') return `Description ${slotNumber}`;
  if (field === 'keyword') return 'Keyword';
  if (field === 'url') return 'Final URL';
  if (field === 'path1') return 'Path 1';
  if (field === 'path2') return 'Path 2';
  if (field === 'campaign') return 'Campaign name';
  if (field === 'ad_group') return 'Ad group name';
  return field;
}

function getFieldLimit(key: string): number | null {
  if (key.startsWith('h')) return 30;
  if (key.startsWith('d')) return 90;
  if (key.startsWith('kw_')) return 80;
  return null;
}

const ERROR_TYPE_LABEL: Record<ErrorType, string> = {
  truncation: 'Over limit',
  missing_placeholder: 'Missing field',
  empty_slot: 'Empty slot',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function CharCount({ value, limit }: { value: string; limit: number }) {
  const over = value.length > limit;
  return (
    <span className={`text-[10px] tabular-nums shrink-0 ${over ? 'text-destructive' : 'text-muted-foreground/40'}`}>
      {value.length}/{limit}
    </span>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/50 mb-3">
      {children}
    </div>
  );
}

function TemplateInput({
  value,
  onChange,
  placeholder,
  limit,
  monospace = true,
  onPaste,
  charCountValue,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  limit?: number;
  monospace?: boolean;
  onPaste?: (e: React.ClipboardEvent) => void;
  charCountValue?: string;
}) {
  const countVal = charCountValue ?? value;
  const over = limit !== undefined && countVal.length > limit;
  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        onPaste={onPaste}
        placeholder={placeholder}
        className={`flex-1 min-w-0 px-3 py-2 text-sm bg-background border rounded-md text-foreground placeholder:text-muted-foreground/30 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50 transition-colors ${monospace ? 'font-mono' : ''} ${over ? 'border-destructive/60' : 'border-border'}`}
      />
      {limit !== undefined && <CharCount value={countVal} limit={limit} />}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

type PreviewViewMode = 'cards' | 'errors' | 'csv';

export default function QuickGenerateView() {
  const { projectId } = useParams<{ projectId: string }>();

  // Template state
  const [campaignName, setCampaignName] = useState('');
  const [adGroupName, setAdGroupName] = useState('');
  const [finalUrl, setFinalUrl] = useState('');
  const [path1, setPath1] = useState('');
  const [path2, setPath2] = useState('');
  const [keywords, setKeywords] = useState<{ text: string; matchTypes: KeywordMatchType[] }[]>([]);
  const [groupingField, setGroupingField] = useState('');

  // Multi-ad state
  interface QGAd { headlines: string[]; descriptions: string[] }
  const [ads, setAds] = useState<QGAd[]>([{ headlines: ['', '', ''], descriptions: ['', ''] }]);
  const [activeAdIdx, setActiveAdIdx] = useState(0); // 0-based
  const headlines = ads[activeAdIdx]?.headlines ?? ['', '', ''];
  const descriptions = ads[activeAdIdx]?.descriptions ?? ['', ''];
  const setHeadlines = (updater: string[] | ((prev: string[]) => string[])) => {
    setAds(prev => prev.map((ad, i) => i === activeAdIdx ? { ...ad, headlines: typeof updater === 'function' ? updater(ad.headlines) : updater } : ad));
  };
  const setDescriptions = (updater: string[] | ((prev: string[]) => string[])) => {
    setAds(prev => prev.map((ad, i) => i === activeAdIdx ? { ...ad, descriptions: typeof updater === 'function' ? updater(ad.descriptions) : updater } : ad));
  };
  const adCount = ads.length;

  // Budget state
  const [budgetEnabled, setBudgetEnabled] = useState(false);
  const [budgetAmount, setBudgetAmount] = useState<number | undefined>(undefined);

  // Data state
  const [rows, setRows] = useState<Record<string, string>[]>([{}]);

  // Template library state
  const [availableTemplates, setAvailableTemplates] = useState<TemplateSet[]>([]);
  const [loadedTemplateId, setLoadedTemplateId] = useState<string | null>(null);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState('Quick Generate');
  const [isSaving, setIsSaving] = useState(false);

  // Generation/preview state
  const [step, setStep] = useState<'build' | 'preview'>('build');
  const [adGroups, setAdGroups] = useState<GeneratedAdGroup[]>([]);
  const [ephemeralTemplate, setEphemeralTemplate] = useState<TemplateSet | null>(null);

  // Preview sub-tab + error management state
  const [previewViewMode, setPreviewViewMode] = useState<PreviewViewMode>('cards');
  const [skippedRows, setSkippedRows] = useState<Set<string>>(new Set());
  const [overrides, setOverrides] = useState<Map<string, Map<string, string>>>(new Map());
  const [editingAgId, setEditingAgId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Map<string, string>>(new Map());
  const [initialEditValues, setInitialEditValues] = useState<Map<string, string>>(new Map());
  const [csvPreview, setCsvPreview] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Load templates ──
  useEffect(() => {
    if (!projectId) return;
    setTemplatesLoading(true);
    getTemplateSetsByProject(projectId)
      .then(ts => { setAvailableTemplates(ts); setTemplatesLoading(false); })
      .catch(() => setTemplatesLoading(false));
  }, [projectId]);

  const loadFromTemplate = (ts: TemplateSet) => {
    const tc = ts.template_content;
    setCampaignName(tc.campaign_name_template ?? '');
    setAdGroupName(tc.ad_group_name_template ?? '');
    setFinalUrl(tc.final_url_template ?? '');
    setPath1(tc.path1_template ?? '');
    setPath2(tc.path2_template ?? '');
    setGroupingField(tc.primary_grouping_field ?? tc.campaign_grouping_field ?? '');

    const effectiveAds = getEffectiveAds(tc);
    const loadedAds: QGAd[] = effectiveAds.map(adDef => ({
      headlines: adDef.headlines
        .map(slot => slot.variants.find(v => v.enabled && v.template_string.trim())?.template_string ?? '')
        .filter((_, i) => i < 15),
      descriptions: adDef.descriptions
        .map(slot => slot.variants.find(v => v.enabled && v.template_string.trim())?.template_string ?? '')
        .filter((_, i) => i < 4),
    }));
    setAds(loadedAds);
    setActiveAdIdx(0);

    setKeywords(
      tc.keywords
        .filter(k => k.variants.some(v => v.enabled && v.template_string.trim()))
        .map(k => ({
          text: k.variants.find(v => v.enabled && v.template_string.trim())?.template_string ?? '',
          matchTypes: k.match_types,
        }))
    );
    setSaveTemplateName(ts.name);
    setLoadedTemplateId(ts.id);
    setShowTemplateLibrary(false);
    toast.success(`Loaded "${ts.name}"`);
  };

  // ── Detected placeholders ──
  const allText = useMemo(() =>
    [campaignName, adGroupName, finalUrl, path1, path2, ...ads.flatMap(a => [...a.headlines, ...a.descriptions]), ...keywords.map(k => k.text)].join(' '),
    [campaignName, adGroupName, finalUrl, path1, path2, ads, keywords]
  );
  const placeholders = useMemo(() => detectPlaceholders([allText]), [allText]);

  // Auto-set grouping field
  useEffect(() => {
    if (placeholders.length > 0 && !placeholders.includes(groupingField)) {
      setGroupingField(placeholders[0]);
    }
    if (placeholders.length === 0) setGroupingField('');
  }, [placeholders.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync row keys when placeholders change
  const phKey = placeholders.join(',');
  useEffect(() => {
    setRows(prev => prev.map(row => Object.fromEntries(placeholders.map(p => [p, row[p] ?? '']))));
  }, [phKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Row helpers ──
  const addRow = () => setRows(prev => [...prev, Object.fromEntries(placeholders.map(p => [p, '']))]);
  const deleteRow = (i: number) => setRows(prev => prev.filter((_, idx) => idx !== i));
  const updateCell = (rowIdx: number, key: string, val: string) =>
    setRows(prev => prev.map((row, i) => i === rowIdx ? { ...row, [key]: val } : row));

  // ── CSV import ──
  const handleCSVFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      const lines = text.trim().split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { toast.error('CSV needs a header row + at least one data row'); return; }
      const headers = parseCSVRow(lines[0]);
      const newRows = lines.slice(1).map(line => {
        const values = parseCSVRow(line);
        return Object.fromEntries(headers.map((h, i) => [h, values[i] ?? '']));
      });
      setRows(newRows);
      const unknown = headers.filter(h => !placeholders.includes(h));
      if (unknown.length > 0 && !allText.trim()) {
        toast(`Tip: use ${unknown.slice(0, 3).map(h => `{${h}}`).join(', ')} in your templates`);
      }
      toast.success(`Imported ${newRows.length} rows from CSV`);
    };
    reader.readAsText(file);
  };

  // ── Validation ──
  const enabledHeadlines = headlines.filter(h => h.trim()).length;
  const enabledDescriptions = descriptions.filter(d => d.trim()).length;
  const filledRows = rows.filter(row => placeholders.length === 0 || placeholders.every(p => row[p]?.trim()));
  const canGenerate = enabledHeadlines >= 1 && enabledDescriptions >= 1 && filledRows.length > 0;

  // ── Generate ──
  const handleGenerate = () => {
    if (!projectId || !canGenerate) return;
    const ts = buildEphemeralTemplateSet(
      projectId, saveTemplateName,
      ads, keywords,
      campaignName, adGroupName, finalUrl, path1, path2, groupingField
    );
    const dataRows: DataRow[] = filledRows.map((row, i) => ({ id: `r${i}`, ...row }));
    const ags = generateAdGroups(ts, dataRows);
    setEphemeralTemplate(ts);
    setAdGroups(ags);
    // Reset error state for new generation
    setSkippedRows(new Set());
    setOverrides(new Map());
    setEditingAgId(null);
    setEditValues(new Map());
    setInitialEditValues(new Map());
    setCsvPreview(null);
    setPreviewViewMode('cards');
    setStep('preview');
  };

  // ── Error management ──
  const exportOptions: ExportOptions = useMemo(() => ({
    skippedRows,
    overrides,
    skippedHeadlineSlots: new Set<number>(),
    skippedDescriptionSlots: new Set<number>(),
  }), [skippedRows, overrides]);

  const isErrorResolved = useCallback((ag: GeneratedAdGroup, err: FieldError): boolean => {
    if (skippedRows.has(ag.rowId)) return true;
    const rowOv = overrides.get(ag.rowId);
    if (!rowOv) return false;
    const key = fieldKey(err);
    if (key == null || !rowOv.has(key)) return false;
    const val = rowOv.get(key)!;
    if (err.type === 'truncation') {
      const limit = getFieldLimit(key);
      return limit == null || val.length <= limit;
    }
    if (err.type === 'missing_placeholder') return !/\{[^}]+\}/.test(val);
    if (err.type === 'empty_slot') return val.trim().length > 0;
    return true;
  }, [skippedRows, overrides]);

  const { totalActiveErrors, totalErroredCities } = useMemo(() => ({
    totalActiveErrors: adGroups.reduce(
      (sum, ag) => sum + ag.errors.filter(e => !isErrorResolved(ag, e)).length, 0,
    ),
    totalErroredCities: adGroups.filter(ag =>
      !skippedRows.has(ag.rowId) && ag.errors.some(e => !isErrorResolved(ag, e))
    ).length,
  }), [adGroups, skippedRows, overrides, isErrorResolved]);

  const erroredGroups = useMemo(() => adGroups.filter(ag => {
    if (ag.errors.length === 0) return false;
    if (skippedRows.has(ag.rowId)) return true;
    return ag.errors.some(e => {
      if (!isErrorResolved(ag, e)) return true;
      const key = fieldKey(e);
      return key != null && overrides.get(ag.rowId)?.has(key);
    });
  }), [adGroups, skippedRows, overrides, isErrorResolved]);

  const applyOverride = (rowId: string, key: string, value: string) => {
    setOverrides(prev => {
      const next = new Map(prev);
      if (!next.has(rowId)) next.set(rowId, new Map());
      next.get(rowId)!.set(key, value);
      return next;
    });
    setCsvPreview(null);
  };

  const skipRow = (rowId: string) => {
    setSkippedRows(prev => { const next = new Set(prev); next.add(rowId); return next; });
    setCsvPreview(null);
  };

  const unskipRow = (rowId: string) => {
    setSkippedRows(prev => { const next = new Set(prev); next.delete(rowId); return next; });
    setCsvPreview(null);
  };

  const enterEditMode = (ag: GeneratedAdGroup) => {
    const vals = new Map<string, string>();
    ag.errors.forEach(err => {
      const key = fieldKey(err);
      if (key && !vals.has(key)) {
        const overrideVal = overrides.get(ag.rowId)?.get(key);
        vals.set(key, overrideVal !== undefined ? overrideVal : getValueFromAg(ag, key));
      }
    });
    setEditValues(vals);
    setInitialEditValues(new Map(vals));
    setEditingAgId(ag.id);
  };

  const confirmEditMode = (ag: GeneratedAdGroup) => {
    editValues.forEach((val, key) => {
      if (val !== initialEditValues.get(key)) applyOverride(ag.rowId, key, val);
    });
    setEditingAgId(null);
    setEditValues(new Map());
    setInitialEditValues(new Map());
  };

  const cancelEditMode = () => {
    setEditingAgId(null);
    setEditValues(new Map());
    setInitialEditValues(new Map());
  };

  // ── Export ──
  const handleExport = async () => {
    if (!projectId || !ephemeralTemplate) return;
    const csv = buildCSV(adGroups, ephemeralTemplate, exportOptions, undefined, budgetEnabled ? { enabled: true, mode: 'uniform', uniformAmount: budgetAmount } : undefined);
    const filename = `quick-generate-${new Date().toISOString().slice(0, 10)}.csv`;
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    const exportableCount = adGroups.length - skippedRows.size;
    const campaignSet = new Set(adGroups.map(ag => ag.campaignName));
    try {
      await createGenerationLog({
        project_id: projectId,
        template_set_id: null,
        template_set_language_code: 'en',
        grouping_field: groupingField || 'none',
        filter_snapshot: { filters: [] },
        totals: {
          campaigns: campaignSet.size,
          ad_groups: exportableCount,
          ads: exportableCount,
          keywords: adGroups.reduce((s, ag) => s + ag.keywords.length, 0),
          callouts: 0,
          sitelinks: 0,
        },
        fallbacks_used: [],
        warnings: [],
        output_filename: filename,
        output_blob: csv,
      });
    } catch (_) {
      // Non-critical
    }

    toast.success(`Downloaded — ${exportableCount.toLocaleString()} ad groups`);
  };

  // ── Save / update template ──
  const handleSaveTemplate = async (updateExisting: boolean) => {
    if (!projectId || !saveTemplateName.trim()) return;
    const ts = buildEphemeralTemplateSet(
      projectId, saveTemplateName,
      ads, keywords,
      campaignName, adGroupName, finalUrl, path1, path2, groupingField
    );
    setIsSaving(true);
    try {
      if (updateExisting && loadedTemplateId) {
        await updateTemplateSet(loadedTemplateId, {
          name: saveTemplateName.trim(),
          template_content: ts.template_content,
        });
        toast.success(`Template "${saveTemplateName}" updated`);
      } else {
        const saved = await createTemplateSet({
          project_id: projectId,
          name: saveTemplateName.trim(),
          language_code: 'en',
          status: 'active',
          generation_settings: { filters: [] },
          template_content: ts.template_content,
        });
        setLoadedTemplateId(saved.id);
        toast.success(`Template "${saveTemplateName}" saved`);
      }
      getTemplateSetsByProject(projectId).then(setAvailableTemplates).catch(() => {});
    } catch (_) {
      toast.error('Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Headline/description helpers ──
  const updateHeadline = (i: number, v: string) => setHeadlines(p => { const n = [...p]; n[i] = v; return n; });
  const removeHeadline = (i: number) => setHeadlines(p => p.filter((_, idx) => idx !== i));
  const updateDescription = (i: number, v: string) => setDescriptions(p => { const n = [...p]; n[i] = v; return n; });
  const removeDescription = (i: number) => setDescriptions(p => p.filter((_, idx) => idx !== i));

  const parsePastedLines = (text: string): string[] =>
    text.split(/[\n\r]+/).map(l => l.trim()).filter(Boolean);

  const pasteHeadlinesFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const lines = parsePastedLines(text).slice(0, 15);
      if (lines.length === 0) return;
      setHeadlines(() => {
        const result: string[] = [];
        for (let i = 0; i < Math.max(lines.length, 3); i++) result.push(lines[i] ?? '');
        return result;
      });
      toast.success(`Pasted ${lines.length} headline${lines.length > 1 ? 's' : ''}`);
    } catch { toast.error('Could not read clipboard'); }
  };

  const pasteDescriptionsFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const lines = parsePastedLines(text).slice(0, 4);
      if (lines.length === 0) return;
      setDescriptions(() => {
        const result: string[] = [];
        for (let i = 0; i < Math.max(lines.length, 2); i++) result.push(lines[i] ?? '');
        return result;
      });
      toast.success(`Pasted ${lines.length} description${lines.length > 1 ? 's' : ''}`);
    } catch { toast.error('Could not read clipboard'); }
  };

  const handleQGPaste = (e: React.ClipboardEvent, type: 'headline' | 'description', index: number) => {
    const text = e.clipboardData.getData('text/plain');
    const lines = parsePastedLines(text);
    if (lines.length <= 1) return;
    e.preventDefault();
    if (type === 'headline') {
      setHeadlines(prev => {
        const result = [...prev];
        for (let i = 0; i < lines.length; i++) {
          const targetIdx = index + i;
          if (targetIdx >= 15) break;
          if (targetIdx < result.length) result[targetIdx] = lines[i];
          else result.push(lines[i]);
        }
        return result;
      });
      toast.success(`Pasted ${lines.length} headlines`);
    } else {
      setDescriptions(prev => {
        const result = [...prev];
        for (let i = 0; i < lines.length; i++) {
          const targetIdx = index + i;
          if (targetIdx >= 4) break;
          if (targetIdx < result.length) result[targetIdx] = lines[i];
          else result.push(lines[i]);
        }
        return result;
      });
      toast.success(`Pasted ${lines.length} descriptions`);
    }
  };
  const parseKeywordMatchType = (raw: string): { text: string; matchTypes: KeywordMatchType[] } => {
    const trimmed = raw.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      return { text: trimmed, matchTypes: ['exact'] };
    }
    if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
      return { text: trimmed, matchTypes: ['phrase'] };
    }
    return { text: trimmed, matchTypes: ['broad'] };
  };

  const stripKeywordWrap = (text: string): string => {
    const t = text.trim();
    if ((t.startsWith('[') && t.endsWith(']')) || (t.startsWith('"') && t.endsWith('"'))) return t.slice(1, -1);
    return t;
  };

  const wrapKeyword = (text: string, matchType: KeywordMatchType): string => {
    const bare = stripKeywordWrap(text);
    if (matchType === 'exact') return `[${bare}]`;
    if (matchType === 'phrase') return `"${bare}"`;
    return bare;
  };

  const pasteKeywordsFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const lines = parsePastedLines(text);
      if (lines.length === 0) return;
      const newKws = lines.map(line => {
        const parsed = parseKeywordMatchType(line);
        return { text: parsed.text, matchTypes: parsed.matchTypes };
      });
      setKeywords(prev => [...prev, ...newKws]);
      toast.success(`Pasted ${lines.length} keyword${lines.length > 1 ? 's' : ''}`);
    } catch { toast.error('Could not read clipboard'); }
  };

  const handleKeywordPaste = (e: React.ClipboardEvent, index: number) => {
    const text = e.clipboardData.getData('text/plain');
    const lines = parsePastedLines(text);
    if (lines.length <= 1) return;
    e.preventDefault();
    setKeywords(prev => {
      const result = [...prev];
      const firstParsed = parseKeywordMatchType(lines[0]);
      result[index] = { text: firstParsed.text, matchTypes: firstParsed.matchTypes };
      const newKws = lines.slice(1).map(line => {
        const parsed = parseKeywordMatchType(line);
        return { text: parsed.text, matchTypes: parsed.matchTypes };
      });
      return [...result, ...newKws];
    });
    toast.success(`Pasted ${lines.length} keywords`);
  };

  const updateKeyword = (i: number, text: string) => setKeywords(p => p.map((k, idx) => idx === i ? { ...k, text } : k));
  const removeKeyword = (i: number) => setKeywords(p => p.filter((_, idx) => idx !== i));
  const toggleMatchType = (i: number, mt: KeywordMatchType) =>
    setKeywords(p => p.map((k, idx) => {
      if (idx !== i) return k;
      const has = k.matchTypes.includes(mt);
      const next = has ? k.matchTypes.filter(m => m !== mt) : [...k.matchTypes, mt];
      if (next.length === 0) return k;
      const newPrimaryMt = next.includes('exact') ? 'exact' : next.includes('phrase') ? 'phrase' : 'broad';
      return { ...k, matchTypes: next, text: wrapKeyword(k.text, newPrimaryMt) };
    }));

  // ── Preview stats ──
  const campaignCount = new Set(adGroups.map(ag => ag.campaignName)).size;
  const keywordCount = adGroups.reduce((s, ag) => s + ag.keywords.length, 0);
  const exportableCount = adGroups.length - skippedRows.size;

  const loadedTemplate = loadedTemplateId ? availableTemplates.find(t => t.id === loadedTemplateId) : null;

  return (
    <div className="app-enter">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-display text-xl font-bold text-foreground flex items-center gap-2 mb-1">
            <Zap className="size-4 text-primary" />
            Quick Generate
          </h1>
          <p className="text-sm text-muted-foreground">
            Generate a Google Ads CSV from custom data — no database required.
          </p>
        </div>
        {step === 'preview' && (
          <Button variant="outline" size="sm" onClick={() => setStep('build')} className="gap-1.5">
            <RefreshCw className="size-3.5" />
            Edit template
          </Button>
        )}
      </div>

      {step === 'build' ? (
        <>
          {/* ── Templates section ── */}
          <div className="mb-5 border border-border rounded-lg overflow-hidden">
            {/* Load row */}
            <div className="px-4 py-3 bg-accent/20 border-b border-border">
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/50 shrink-0 w-10">Load</span>
                {templatesLoading ? (
                  <span className="text-xs text-muted-foreground/40 italic">Loading…</span>
                ) : availableTemplates.length === 0 ? (
                  <span className="text-xs text-muted-foreground/40 italic">No templates in this project yet</span>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    {/* Collapsed: show loaded template or toggle button */}
                    {!showTemplateLibrary ? (
                      <>
                        {loadedTemplate && (
                          <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-primary/10 border border-primary/25 text-primary font-medium">
                            <Check className="size-3" />
                            {loadedTemplate.name}
                          </span>
                        )}
                        <button
                          onClick={() => setShowTemplateLibrary(true)}
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                        >
                          <ChevronRight className="size-3.5" />
                          {loadedTemplate ? 'Change template' : `${availableTemplates.length} template${availableTemplates.length !== 1 ? 's' : ''} available`}
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="flex flex-wrap gap-1.5 min-w-0">
                          {availableTemplates.map(t => (
                            <button
                              key={t.id}
                              onClick={() => loadFromTemplate(t)}
                              className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-all ${
                                loadedTemplateId === t.id
                                  ? 'bg-primary/10 border-primary/30 text-primary font-medium'
                                  : 'bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'
                              }`}
                            >
                              {loadedTemplateId === t.id && <Check className="size-3" />}
                              {t.name}
                            </button>
                          ))}
                        </div>
                        <button
                          onClick={() => setShowTemplateLibrary(false)}
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors ml-1"
                        >
                          <ChevronDown className="size-3.5" />
                          Collapse
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Save row */}
            <div className="px-4 py-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/50 shrink-0 w-10">Save</span>
                <input
                  type="text"
                  value={saveTemplateName}
                  onChange={e => setSaveTemplateName(e.target.value)}
                  placeholder="Template name"
                  className="flex-1 min-w-[160px] max-w-xs px-2.5 py-1.5 text-xs bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground/30 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50 transition-colors"
                />
                {loadedTemplateId && loadedTemplate && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs px-3 shrink-0"
                    disabled={isSaving || !saveTemplateName.trim()}
                    onClick={() => handleSaveTemplate(true)}
                  >
                    {isSaving ? 'Saving…' : `Update "${loadedTemplate.name}"`}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={loadedTemplateId ? 'ghost' : 'outline'}
                  className="h-7 text-xs px-3 shrink-0"
                  disabled={isSaving || !saveTemplateName.trim()}
                  onClick={() => handleSaveTemplate(false)}
                >
                  {isSaving ? 'Saving…' : loadedTemplateId ? 'Save as new' : 'Save template'}
                </Button>
              </div>
            </div>
          </div>

          {/* ── Builder + Data ── */}
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6">

            {/* Left: Template builder */}
            <div className="space-y-5">

              {/* Campaign structure */}
              <div className="border border-border rounded-lg p-5 space-y-4">
                <SectionLabel>Campaign structure</SectionLabel>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1.5">Campaign name</label>
                    <TemplateInput value={campaignName} onChange={setCampaignName} placeholder="{city} — Jobs" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1.5">Ad group name</label>
                    <TemplateInput value={adGroupName} onChange={setAdGroupName} placeholder="{job} in {city}" />
                  </div>
                </div>

                {placeholders.length > 1 && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1.5">Group campaigns by</label>
                    <select
                      value={groupingField}
                      onChange={e => setGroupingField(e.target.value)}
                      className="w-full text-sm px-3 py-2 bg-background border border-border rounded-md text-foreground font-mono focus:outline-none focus:ring-1 focus:ring-primary/50 transition-colors"
                    >
                      {placeholders.map(p => <option key={p} value={p}>{`{${p}}`}</option>)}
                    </select>
                  </div>
                )}

                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1.5">Final URL</label>
                  <TemplateInput value={finalUrl} onChange={setFinalUrl} placeholder="https://example.com/{city}" monospace={false} />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1.5">Display path 1</label>
                    <TemplateInput value={path1} onChange={setPath1} placeholder="{city}" limit={CHARACTER_LIMITS.display_path} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1.5">Display path 2</label>
                    <TemplateInput value={path2} onChange={setPath2} placeholder="{job}" limit={CHARACTER_LIMITS.display_path} />
                  </div>
                </div>
              </div>

              {/* Budget */}
              <div className="border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <SectionLabel>Campaign budget</SectionLabel>
                    <p className="text-[11px] text-muted-foreground -mt-2">
                      {budgetEnabled ? 'Daily budget included in CSV' : 'No budget in CSV export'}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={budgetEnabled} onChange={e => setBudgetEnabled(e.target.checked)} className="sr-only peer" />
                    <div className="w-9 h-5 bg-muted rounded-full peer peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full" />
                  </label>
                </div>
                {budgetEnabled && (
                  <div className="mt-3 pt-3 border-t border-border/40">
                    <label className="text-xs text-muted-foreground block mb-1.5">Daily budget (all campaigns)</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={budgetAmount ?? ''}
                      onChange={e => setBudgetAmount(e.target.value ? parseFloat(e.target.value) : undefined)}
                      placeholder="e.g., 20.00"
                      className="w-full px-3 py-2 text-sm font-mono bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground/30 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50 transition-colors"
                    />
                  </div>
                )}
              </div>

              {/* Ad selector */}
              <div className="flex items-center gap-2 px-1">
                <div className="flex items-center gap-1 bg-muted/30 rounded-md p-0.5 border border-border/40">
                  {ads.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveAdIdx(idx)}
                      className={`px-3 py-1 text-xs font-mono rounded transition-all ${
                        activeAdIdx === idx
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      Ad {idx + 1}
                    </button>
                  ))}
                </div>
                {adCount < 3 && (
                  <div className="relative">
                    <button
                      onClick={() => {
                        setAds(prev => [...prev, { headlines: ['', '', ''], descriptions: ['', ''] }]);
                        setActiveAdIdx(ads.length);
                        toast.success(`Ad ${ads.length + 1} created`);
                      }}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-muted-foreground/50 hover:text-primary border border-dashed border-border/40 hover:border-primary/40 rounded transition-colors"
                    >
                      <Plus className="size-3" /> Add ad
                    </button>
                  </div>
                )}
                {adCount > 1 && activeAdIdx > 0 && (
                  <button
                    onClick={() => {
                      setAds(prev => prev.filter((_, i) => i !== activeAdIdx));
                      setActiveAdIdx(Math.min(activeAdIdx, ads.length - 2));
                      toast.success('Ad removed');
                    }}
                    className="ml-auto flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-muted-foreground/40 hover:text-destructive border border-border/30 hover:border-destructive/40 rounded transition-colors"
                  >
                    <Trash2 className="size-3" /> Remove
                  </button>
                )}
              </div>

              {/* Headlines */}
              <div className="border border-border rounded-lg p-5">
                <div className="flex items-center justify-between mb-3">
                  <SectionLabel>Headlines</SectionLabel>
                  <div className="flex items-center gap-2">
                    <button
                      className="text-[9px] font-mono text-muted-foreground/40 hover:text-primary border border-border/40 rounded px-1.5 py-0.5 transition-colors flex items-center gap-1"
                      title="Paste headlines from clipboard (one per line)"
                      onClick={pasteHeadlinesFromClipboard}
                    ><ClipboardPaste className="size-2.5" />Paste</button>
                    <span className="text-[10px] font-mono text-muted-foreground/40">{enabledHeadlines}/15</span>
                  </div>
                </div>
                <div className="space-y-2">
                  {headlines.map((h, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-muted-foreground/30 w-4 text-right shrink-0">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <TemplateInput
                          value={h}
                          onChange={v => updateHeadline(i, v)}
                          onPaste={e => handleQGPaste(e, 'headline', i)}
                          placeholder={`Headline ${i + 1}`}
                          limit={CHARACTER_LIMITS.headline}
                        />
                      </div>
                      {headlines.length > 1 && (
                        <button onClick={() => removeHeadline(i)} className="shrink-0 p-1 rounded text-muted-foreground/30 hover:text-muted-foreground transition-colors">
                          <X className="size-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {headlines.length < 15 && (
                  <button
                    onClick={() => setHeadlines(p => [...p, ''])}
                    className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Plus className="size-3.5" /> Add headline
                  </button>
                )}
              </div>

              {/* Descriptions */}
              <div className="border border-border rounded-lg p-5">
                <div className="flex items-center justify-between mb-3">
                  <SectionLabel>Descriptions</SectionLabel>
                  <div className="flex items-center gap-2">
                    <button
                      className="text-[9px] font-mono text-muted-foreground/40 hover:text-primary border border-border/40 rounded px-1.5 py-0.5 transition-colors flex items-center gap-1"
                      title="Paste descriptions from clipboard (one per line)"
                      onClick={pasteDescriptionsFromClipboard}
                    ><ClipboardPaste className="size-2.5" />Paste</button>
                    <span className="text-[10px] font-mono text-muted-foreground/40">{enabledDescriptions}/4</span>
                  </div>
                </div>
                <div className="space-y-2">
                  {descriptions.map((d, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-muted-foreground/30 w-4 text-right shrink-0">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <TemplateInput
                          value={d}
                          onChange={v => updateDescription(i, v)}
                          onPaste={e => handleQGPaste(e, 'description', i)}
                          placeholder={`Description ${i + 1}`}
                          limit={CHARACTER_LIMITS.description}
                        />
                      </div>
                      {descriptions.length > 1 && (
                        <button onClick={() => removeDescription(i)} className="shrink-0 p-1 rounded text-muted-foreground/30 hover:text-muted-foreground transition-colors">
                          <X className="size-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {descriptions.length < 4 && (
                  <button
                    onClick={() => setDescriptions(p => [...p, ''])}
                    className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Plus className="size-3.5" /> Add description
                  </button>
                )}
              </div>

              {/* Keywords */}
              <div className="border border-border rounded-lg p-5">
                <div className="flex items-center justify-between mb-1">
                  <SectionLabel>Keywords</SectionLabel>
                  <button
                    className="text-[9px] font-mono text-muted-foreground/40 hover:text-primary border border-border/40 rounded px-1.5 py-0.5 transition-colors flex items-center gap-1"
                    title="Paste keywords from clipboard (one per line)"
                    onClick={pasteKeywordsFromClipboard}
                  ><ClipboardPaste className="size-2.5" />Paste</button>
                </div>
                {keywords.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {keywords.map((kw, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <TemplateInput
                            value={kw.text}
                            onChange={v => updateKeyword(i, v)}
                            onPaste={e => handleKeywordPaste(e, i)}
                            placeholder="{job} {city}"
                            limit={CHARACTER_LIMITS.keyword}
                            charCountValue={stripKeywordWrap(kw.text)}
                          />
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {(['exact', 'phrase', 'broad'] as KeywordMatchType[]).map(mt => (
                            <button
                              key={mt}
                              onClick={() => toggleMatchType(i, mt)}
                              title={mt}
                              className={`text-[10px] px-1.5 py-0.5 rounded font-mono border transition-colors ${
                                kw.matchTypes.includes(mt)
                                  ? 'border-primary/40 bg-primary/10 text-primary'
                                  : 'border-border text-muted-foreground/40 hover:border-muted-foreground/40'
                              }`}
                            >
                              {mt[0]}
                            </button>
                          ))}
                        </div>
                        <button onClick={() => removeKeyword(i)} className="shrink-0 p-1 rounded text-muted-foreground/30 hover:text-muted-foreground transition-colors">
                          <X className="size-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => setKeywords(p => [...p, { text: '', matchTypes: ['broad'] }])}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  <Plus className="size-3.5" /> Add keyword
                </button>
              </div>
            </div>

            {/* Right: Data entry — full sticky wrapper */}
            <div className="sticky top-4 space-y-3">
              <div className="border border-border rounded-lg p-5">

                {/* Header row */}
                <div className="flex items-center justify-between mb-4">
                  <SectionLabel>Data</SectionLabel>
                  <div className="flex items-center gap-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={e => { const f = e.target.files?.[0]; if (f) handleCSVFile(f); e.target.value = ''; }}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      <Upload className="size-3.5" /> Import CSV
                    </button>
                  </div>
                </div>

                {/* Placeholder badges */}
                {placeholders.length > 0 ? (
                  <div className="mb-4">
                    <div className="text-[10px] text-muted-foreground/40 uppercase tracking-wider mb-2">Detected placeholders</div>
                    <div className="flex flex-wrap gap-1.5">
                      {placeholders.map(p => (
                        <span key={p} className="inline-flex items-center text-[11px] font-mono px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                          {`{${p}}`}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mb-4 py-5 px-4 rounded-lg border border-dashed border-border bg-accent/20 text-center">
                    <p className="text-xs text-muted-foreground/50">
                      Add <span className="font-mono text-primary">{'{placeholder}'}</span> to any template field
                      to define the columns for your data
                    </p>
                  </div>
                )}

                {/* Data grid */}
                {placeholders.length > 0 && (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr>
                            {placeholders.map(p => (
                              <th key={p} className="pb-2 pr-2 text-left">
                                <span className="text-[10px] font-mono font-semibold text-muted-foreground/50 uppercase tracking-wider">{p}</span>
                              </th>
                            ))}
                            <th className="pb-2 w-5" />
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, i) => (
                            <tr key={i} className="group">
                              {placeholders.map(p => (
                                <td key={p} className="pr-2 pb-1.5">
                                  <input
                                    type="text"
                                    value={row[p] ?? ''}
                                    onChange={e => updateCell(i, p, e.target.value)}
                                    placeholder="—"
                                    className="w-full px-2.5 py-1.5 text-xs font-mono bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40 placeholder:text-muted-foreground/20 transition-colors"
                                  />
                                </td>
                              ))}
                              <td className="pb-1.5">
                                <button
                                  onClick={() => deleteRow(i)}
                                  className="opacity-0 group-hover:opacity-100 p-1 rounded text-muted-foreground/30 hover:text-muted-foreground transition-all"
                                >
                                  <X className="size-3" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <button
                      onClick={addRow}
                      className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      <Plus className="size-3.5" /> Add row
                    </button>

                    <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">
                        <span className="text-foreground font-medium">{filledRows.length}</span> of {rows.length} rows complete
                      </span>
                      {filledRows.length < rows.length && (
                        <span className="text-amber-400/80 text-[10px]">Incomplete rows will be skipped</span>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Validation + generate */}
              <div className="space-y-2.5">
                {!canGenerate && (enabledHeadlines < 1 || enabledDescriptions < 1 || filledRows.length === 0) && (
                  <div className="flex items-start gap-2 text-xs text-muted-foreground p-3 rounded-md bg-accent/30 border border-border">
                    <AlertCircle className="size-3.5 shrink-0 mt-0.5" />
                    <span>
                      {[
                        enabledHeadlines < 1 && 'at least 1 headline',
                        enabledDescriptions < 1 && 'at least 1 description',
                        filledRows.length === 0 && 'at least 1 complete data row',
                      ].filter(Boolean).join(', ')} required to generate
                    </span>
                  </div>
                )}
                <Button onClick={handleGenerate} disabled={!canGenerate} className="w-full gap-2">
                  <Zap className="size-4" />
                  {filledRows.length > 0
                    ? `Generate ${filledRows.length} ad group${filledRows.length !== 1 ? 's' : ''}`
                    : 'Generate'}
                </Button>
              </div>
            </div>
          </div>
        </>

      ) : (
        /* ── Preview ── */
        <div className="space-y-5">

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Campaigns', value: campaignCount },
              { label: 'Ad groups', value: adGroups.length },
              { label: 'Keywords', value: keywordCount },
              { label: 'Issues', value: totalActiveErrors, warn: totalActiveErrors > 0 },
            ].map(({ label, value, warn }) => (
              <div key={label} className="border border-border rounded-lg p-4 text-center">
                <div className={`text-2xl font-display font-bold ${warn ? 'text-amber-400' : 'text-foreground'}`}>
                  {value}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
              </div>
            ))}
          </div>

          {/* Sub-tab bar + export */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex gap-1 p-1 bg-muted rounded-lg">
              {([
                { id: 'cards' as PreviewViewMode, icon: null, label: 'Preview' },
                { id: 'errors' as PreviewViewMode, icon: AlertTriangle, label: 'Issues', count: totalActiveErrors },
                { id: 'csv' as PreviewViewMode, icon: FileCode2, label: 'CSV' },
              ] as Array<{ id: PreviewViewMode; icon: React.ComponentType<{ className?: string }> | null; label: string; count?: number }>).map(({ id, icon: Icon, label, count }) => (
                <button
                  key={id}
                  onClick={() => {
                    setPreviewViewMode(id);
                    if (id === 'csv' && !csvPreview && ephemeralTemplate) {
                      setCsvPreview(buildCSV(adGroups, ephemeralTemplate, exportOptions, undefined, budgetEnabled ? { enabled: true, mode: 'uniform', uniformAmount: budgetAmount } : undefined));
                    }
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    previewViewMode === id
                      ? 'bg-card shadow text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {Icon && <Icon className={`size-3.5 ${id === 'errors' && count ? 'text-amber-500' : ''}`} />}
                  {label}
                  {id === 'errors' && count != null && count > 0 && (
                    <span className="ml-0.5 px-1.5 py-0.5 rounded-full text-xs bg-amber-500/15 text-amber-600 dark:text-amber-400 font-medium">
                      {count.toLocaleString()}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              {skippedRows.size > 0 && (
                <span className="text-xs text-muted-foreground">{skippedRows.size} skipped</span>
              )}
              <Button onClick={handleExport} size="sm" className="gap-1.5">
                <Download className="size-3.5" />
                Export CSV ({exportableCount.toLocaleString()})
              </Button>
            </div>
          </div>

          {/* ── Cards / Preview tab ── */}
          {previewViewMode === 'cards' && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="px-5 py-3 border-b border-border bg-accent/30">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/50">
                  Preview — first {Math.min(adGroups.length, 10)} ad groups
                </span>
              </div>
              <div className="divide-y divide-border">
                {adGroups.slice(0, 10).map((ag, i) => {
                  const isSkipped = skippedRows.has(ag.rowId);
                  const rowOv = overrides.get(ag.rowId) ?? new Map<string, string>();
                  const ov = (key: string, fallback: string) => rowOv.get(key) ?? fallback;
                  const activeErrors = ag.errors.filter(e => !isErrorResolved(ag, e));
                  return (
                    <div key={i} className={`px-5 py-4 ${isSkipped ? 'opacity-40' : ''}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-mono text-muted-foreground/40 truncate">{ov('campaign', ag.campaignName)}</span>
                        <span className="text-muted-foreground/20 shrink-0">›</span>
                        <span className="text-[10px] font-mono text-muted-foreground/50 truncate">{ov('ad_group', ag.adGroupName)}</span>
                        {isSkipped && <Badge variant="outline" className="text-[10px] text-muted-foreground shrink-0">Skipped</Badge>}
                        {!isSkipped && activeErrors.length > 0 && (
                          <Badge variant="outline" className="text-[10px] border-amber-400/30 text-amber-400 shrink-0">
                            {activeErrors.length} issue{activeErrors.length !== 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                        {ag.headlines.filter(h => h.text).slice(0, 6).map((h, hi) => (
                          <span key={hi} className="text-xs font-medium text-primary">{ov(`h${h.slotNumber}`, h.text)}</span>
                        ))}
                      </div>
                      {ag.descriptions[0]?.text && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{ov('d1', ag.descriptions[0].text)}</p>
                      )}
                    </div>
                  );
                })}
                {adGroups.length > 10 && (
                  <div className="px-5 py-3 text-xs text-muted-foreground/40 text-center">
                    + {adGroups.length - 10} more ad groups in the export
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Issues tab ── */}
          {previewViewMode === 'errors' && (
            <div className="space-y-3">
              {totalActiveErrors > 0 && (
                <p className="text-xs text-muted-foreground px-1">
                  <span className="font-medium text-amber-600 dark:text-amber-400">{totalActiveErrors.toLocaleString()} issue{totalActiveErrors !== 1 ? 's' : ''}</span>
                  {' across '}
                  <span className="font-medium text-foreground">{totalErroredCities.toLocaleString()} row{totalErroredCities !== 1 ? 's' : ''}</span>
                </p>
              )}
              {erroredGroups.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12 gap-3">
                    <CheckCircle2 className="size-10 text-green-500" />
                    <p className="font-medium">No issues — ready to export</p>
                  </CardContent>
                </Card>
              ) : (
                erroredGroups.map(ag => {
                  const isSkipped = skippedRows.has(ag.rowId);
                  const isEditing = editingAgId === ag.id;
                  const activeErrors = ag.errors.filter(e => !isErrorResolved(ag, e));
                  const resolvedErrors = ag.errors.filter(e => {
                    if (!isErrorResolved(ag, e)) return false;
                    const key = fieldKey(e);
                    return key != null && overrides.get(ag.rowId)?.has(key);
                  });
                  const displayErrors = [...activeErrors, ...resolvedErrors];

                  return (
                    <Card key={ag.id} className={`border-amber-500/20 ${isSkipped ? 'opacity-60' : ''}`}>
                      <div className="flex items-center justify-between px-5 py-3 border-b border-border/50">
                        <div>
                          <p className="text-xs text-muted-foreground">{ag.campaignName}</p>
                          <p className="text-sm font-semibold">{ag.adGroupName}</p>
                          {!isSkipped && activeErrors.length === 0 && resolvedErrors.length > 0 && (
                            <p className="text-xs text-green-600 dark:text-green-400 mt-0.5 flex items-center gap-1">
                              <CheckCircle2 className="size-3" /> All issues resolved
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {isSkipped ? (
                            <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground hover:text-foreground" onClick={() => unskipRow(ag.rowId)}>
                              <RotateCcw className="size-3 mr-1" />Undo skip
                            </Button>
                          ) : (
                            <>
                              {!isEditing && (
                                <Button variant="ghost" size="sm" className="h-7 text-xs text-primary hover:text-primary hover:bg-primary/10" onClick={() => enterEditMode(ag)}>
                                  <Pencil className="size-3 mr-1" />Edit row
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive hover:text-destructive" onClick={() => skipRow(ag.rowId)}>
                                <SkipForward className="size-3 mr-1" />Skip row
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      {isSkipped ? (
                        <div className="px-5 py-4 text-sm text-muted-foreground">This row will be excluded from the CSV export.</div>
                      ) : (
                        <div className="px-5 py-3 space-y-4">
                          {displayErrors.map((err, ei) => {
                            const key = fieldKey(err);
                            const overrideVal = key ? overrides.get(ag.rowId)?.get(key) : undefined;
                            const rawVal = key ? getValueFromAg(ag, key) : '';
                            const displayVal = overrideVal !== undefined ? overrideVal : rawVal;
                            const isSkippedField = overrideVal === '';
                            const limit = key ? getFieldLimit(key) : null;
                            const isFit = overrideVal != null && overrideVal !== '' && (limit == null || overrideVal.length <= limit);
                            const fieldEditVal = key ? (editValues.get(key) ?? displayVal) : displayVal;

                            const borderColor = isSkippedField
                              ? 'border-muted-foreground/25'
                              : isFit ? 'border-green-500/50' : 'border-amber-400/50';

                            return (
                              <div key={ei} className={`pl-3 border-l-2 ${borderColor} space-y-1.5`}>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-medium">{fieldLabel(err.field, err.slotNumber)}</span>
                                  {isSkippedField ? (
                                    <Badge variant="outline" className="text-xs text-muted-foreground">Skipped</Badge>
                                  ) : isFit ? (
                                    <Badge variant="outline" className="text-xs text-green-700 dark:text-green-400 border-green-500/40 bg-green-500/5">
                                      <Check className="size-2.5 mr-1" />Fixed
                                    </Badge>
                                  ) : (
                                    <>
                                      <Badge variant="outline" className="text-xs text-amber-700 dark:text-amber-400 border-amber-500/40 bg-amber-500/5">
                                        {ERROR_TYPE_LABEL[err.type] ?? err.type}
                                      </Badge>
                                      <span className="text-xs text-muted-foreground">{err.message}</span>
                                    </>
                                  )}
                                </div>
                                {isEditing && key ? (
                                  <div className="space-y-1">
                                    <Input
                                      value={fieldEditVal}
                                      onChange={e => setEditValues(prev => new Map(prev).set(key, e.target.value))}
                                      className="h-7 text-xs font-mono"
                                      placeholder="Replacement value for this row…"
                                    />
                                    {limit && (
                                      <p className={`text-xs ${fieldEditVal.length > limit ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                                        {fieldEditVal.length}/{limit} chars
                                        {fieldEditVal.length > limit && ` — ${fieldEditVal.length - limit} over`}
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  displayVal && !isSkippedField && (
                                    <p className={`text-xs font-mono rounded px-2 py-1 truncate max-w-prose ${
                                      isFit
                                        ? 'text-green-700 dark:text-green-400 bg-green-500/5 border border-green-500/20'
                                        : 'text-foreground/70 bg-muted/50'
                                    }`}>
                                      "{displayVal}"
                                    </p>
                                  )
                                )}
                              </div>
                            );
                          })}
                          {isEditing && (
                            <div className="flex gap-2 pt-2 border-t border-border/50">
                              <Button size="sm" className="h-7 text-xs" onClick={() => confirmEditMode(ag)}>
                                <Check className="size-3 mr-1" />Apply
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={cancelEditMode}>
                                <X className="size-3 mr-1" />Cancel
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })
              )}
            </div>
          )}

          {/* ── CSV tab ── */}
          {previewViewMode === 'csv' && (
            <Card>
              <CardContent className="space-y-3 pt-5">
                {!csvPreview ? (
                  <div className="flex flex-col items-center gap-3 py-8">
                    <p className="text-sm text-muted-foreground">Build a preview of the CSV with current overrides applied.</p>
                    <Button
                      variant="outline"
                      onClick={() => ephemeralTemplate && setCsvPreview(buildCSV(adGroups, ephemeralTemplate, exportOptions, undefined, budgetEnabled ? { enabled: true, mode: 'uniform', uniformAmount: budgetAmount } : undefined))}
                    >
                      <FileCode2 className="size-4 mr-2" />Build CSV Preview
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">
                        Showing first 20 rows of {csvPreview.split('\n').length.toLocaleString()} total lines
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost" size="sm" className="text-xs h-7"
                          onClick={() => ephemeralTemplate && setCsvPreview(buildCSV(adGroups, ephemeralTemplate, exportOptions, undefined, budgetEnabled ? { enabled: true, mode: 'uniform', uniformAmount: budgetAmount } : undefined))}
                        >
                          Rebuild
                        </Button>
                        <Button
                          variant="ghost" size="sm" className="text-xs h-7"
                          onClick={() => { navigator.clipboard.writeText(csvPreview); toast.success('Copied to clipboard'); }}
                        >
                          Copy all
                        </Button>
                      </div>
                    </div>
                    <pre className="text-xs font-mono bg-muted/50 rounded-md p-3 overflow-x-auto max-h-[400px] overflow-y-auto leading-5">
                      {csvPreview.split('\n').slice(0, 21).join('\n')}
                    </pre>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
